import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { LOGO_URL } from "@/lib/content";

/**
 * Full-bleed brand-mark strip showing the Trilogy Earthworks logo at massive scale.
 * Sits between the hero and the marquee — a signature brand moment.
 */
export default function BrandMark() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["6%", "-6%"]);

  return (
    <section
      ref={ref}
      className="relative bg-[color:var(--te-bg)] border-t border-white/10 overflow-hidden py-16 md:py-24 lg:py-28"
      data-testid="brandmark-section"
      aria-label="Trilogy Earthworks brand"
    >
      <div className="max-w-[1600px] mx-auto px-5 md:px-10 flex flex-col items-center">
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          style={{ y }}
          className="w-full flex items-center justify-center"
        >
          <img
            src={LOGO_URL}
            alt="Trilogy Earthworks"
            className="w-full max-w-[1400px] h-auto object-contain block select-none"
            draggable={false}
            data-testid="brandmark-logo"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.9, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="mt-6 md:mt-10 flex flex-wrap items-center justify-center gap-x-6 md:gap-x-10 gap-y-2"
          data-testid="brandmark-motto"
        >
          {["Pride", "Proficiency", "Passion"].map((word, i) => (
            <div key={word} className="flex items-center gap-6 md:gap-10">
              <span
                className="font-display font-black uppercase tracking-[0.28em] md:tracking-[0.36em] text-white"
                style={{ fontSize: "clamp(1rem, 2.2vw, 1.75rem)" }}
              >
                {word}
              </span>
              {i < 2 && (
                <span
                  aria-hidden
                  className="w-1.5 h-1.5 md:w-2 md:h-2 bg-[color:var(--te-primary)] rotate-45"
                />
              )}
            </div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.8, delay: 0.55 }}
          className="mt-8 md:mt-12 flex items-center gap-4"
        >
          <span className="h-px w-14 bg-[color:var(--te-primary)]" />
          <span className="text-[11px] md:text-xs uppercase tracking-[0.32em] text-[color:var(--te-primary)] font-display">
            Est. Rockhampton · Central Queensland
          </span>
          <span className="h-px w-14 bg-[color:var(--te-primary)]" />
        </motion.div>
      </div>
    </section>
  );
}
